import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { loginService } from '@/features/user/services/loginService';
import { cartService } from '@/features/cart/services/cartService';
import { likeService } from '@/features/likes/services/likeService';
import type { LoginFormData } from '@/features/user/types/types';

export const useLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: setUser, setError, clearError, refetch: refetchAuth } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const login = async (formData: LoginFormData) => {
    setIsSubmitting(true);
    clearError();

    try {
      const response = await loginService.login({
        email: formData.email,
        password: formData.password,
      });

      // Set user in store
      setUser(response.data.user);

      // Claim guest likes FIRST (before cart claim removes guest cookies)
      await likeService.claimLikes().catch((error) => {
        // Don't fail login if claim fails - log and continue
        console.error('Failed to claim guest likes after login:', error);
      });

      // Claim guest cart (moves cart ownerships from guest to real user)
      // Note: This removes guest cookies, so must be called after claimLikes
      await cartService.claimGuestCart().catch((error) => {
        // Don't fail login if claim fails - log and continue
        console.error('Failed to claim guest cart after login:', error);
      });

      // Cart count will auto-refresh via header's stale-while-revalidate

      // Refetch AuthProvider to sync with new authentication state
      await refetchAuth();

      // Navigate to returnUrl if present, otherwise profile page
      const returnUrl = searchParams.get('returnUrl');
      navigate(returnUrl || '/profile');

    } catch (error: unknown) {
      console.error('Login error:', error);

      // Handle different error types
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          setError('Invalid email or password');
        } else if (axiosError.response?.status === 400) {
          setError('Please check your email and password');
        } else {
          setError('Login failed. Please try again.');
        }
      } else {
        setError('Login failed. Please try again.');
      }

      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    login,
    isSubmitting,
  };
};
